import { DownOutlined } from '@ant-design/icons';
import {
  DrawerForm,
  ModalForm,
  PageContainer,
  ProFormText,
  ProFormTextArea,
} from '@ant-design/pro-components';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { App, Button, Card, Dropdown, Flex, Input, List, Space, Typography } from 'antd';
import dayjs from 'dayjs';
import PropTypes from 'prop-types';
import { useState } from 'react';
import { useOrganizationPath } from '@/common/hooks/use-organization-path';
import { useQueryParams } from '@/common/hooks/use-query-params';
import ScriptArcPreview from '../components/ScriptArcPreview';
import {
  getIpCreatorRecord,
  queryIpCreatorRecords,
  removeIpCreatorRecord,
  updateIpCreatorRecord,
} from '../services/ip-creator';

const { Search } = Input;

const resource = 'script-arcs';
const resourceName = '视频脚本';
const scriptStartHeading = '## 完整口播脚本';
const scriptEndHeading = '## 素材清单';

export const extractSpeechScript = (script) => {
  const normalizedScript = String(script || '');
  const startIndex = normalizedScript.indexOf(scriptStartHeading);
  const endIndex = normalizedScript.indexOf(scriptEndHeading);
  if (startIndex < 0 || endIndex <= startIndex) {
    return '';
  }
  return normalizedScript.slice(startIndex + scriptStartHeading.length, endIndex).trim();
};

export const renderScriptTitle = (record) => (
  <Typography.Text ellipsis={{ tooltip: record.title }}>{record.title || '-'}</Typography.Text>
);

const ListContent = ({ data }) => (
  <Space size="large" wrap>
    <Space vertical size="small">
      <Typography.Text type="secondary">创作者</Typography.Text>
      <Typography.Text>{data.userName || '-'}</Typography.Text>
    </Space>
    <Space vertical size="small">
      <Typography.Text type="secondary">更新时间</Typography.Text>
      <Typography.Text>
        {data.updatedAt ? dayjs(data.updatedAt).format('YYYY-MM-DD HH:mm') : '-'}
      </Typography.Text>
    </Space>
  </Space>
);

ListContent.propTypes = {
  data: PropTypes.shape({
    updatedAt: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    userName: PropTypes.string,
  }),
};

const ScriptArcs = () => {
  const [detailRecord, setDetailRecord] = useState(null);
  const [editRecord, setEditRecord] = useState(null);
  const [query, setQuery, queryString] = useQueryParams();
  const queryClient = useQueryClient();
  const { organizationId } = useOrganizationPath();
  const { message, modal } = App.useApp();
  const title = Array.isArray(query.title) ? query.title.at(-1) : query.title || '';

  const reload = () => queryClient.invalidateQueries({ queryKey: ['ip-creator', resource] });

  const { data: listResult, isLoading: loading } = useQuery({
    queryKey: ['ip-creator', resource, organizationId, queryString],
    queryFn: () =>
      queryIpCreatorRecords(resource, {
        organizationId,
        title: title || undefined,
        current: 1,
        pageSize: 50,
      }),
  });

  const list = listResult?.data || [];

  const { mutateAsync: updateRecord, isPending: updating } = useMutation({
    mutationFn: (payload) => updateIpCreatorRecord(resource, payload),
    onSuccess: () => {
      message.success(`${resourceName}已更新`);
      reload();
    },
    onError: () => message.error(`${resourceName}更新失败，请重试`),
  });

  const { mutateAsync: removeRecord, isPending: removing } = useMutation({
    mutationFn: (payload) => removeIpCreatorRecord(resource, payload),
    onSuccess: () => {
      message.success(`${resourceName}已移除`);
      reload();
    },
    onError: () => message.error(`${resourceName}移除失败，请重试`),
  });

  const { mutateAsync: getRecord } = useMutation({
    mutationFn: (payload) => getIpCreatorRecord(resource, payload),
    onSuccess: (payload) => setDetailRecord(payload?.data || payload),
    onError: () => message.error(`${resourceName}详情读取失败，请重试`),
  });

  const updateSearchParam = (key, value) => {
    setQuery({ [key]: value || undefined });
  };

  const removeRecordWithConfirm = (record) => {
    modal.confirm({
      title: `移除${resourceName}`,
      content: `确认移除 ${record.title || record.id}？`,
      okText: '确认',
      cancelText: '取消',
      onOk: () => removeRecord({ data: { id: record.id, organizationId } }),
    });
  };

  const handleMoreAction = (key, record) => {
    if (key === 'edit') {
      setEditRecord(record);
      return;
    }
    if (key === 'remove') {
      removeRecordWithConfirm(record);
    }
  };

  const renderFormFields = () => (
    <>
      <ProFormText
        name="topicId"
        label="选题ID"
        rules={[{ required: true, message: '请输入选题ID' }]}
      />
      <ProFormText name="title" label="标题" rules={[{ required: true, message: '请输入标题' }]} />
      <ProFormTextArea
        name="script"
        label="脚本"
        rules={[{ required: true, message: '请输入脚本' }]}
      />
    </>
  );

  return (
    <PageContainer content="基于选题生成可进入拍摄与素材准备阶段的视频脚本">
      <Flex vertical gap="large">
        <Card variant="borderless" title={resourceName}>
          <Flex gap="middle" wrap>
            <Search
              allowClear
              defaultValue={title}
              key={title}
              placeholder="标题"
              onSearch={(value) => updateSearchParam('title', value)}
              variant="filled"
            />
          </Flex>
          <List
            size="large"
            rowKey="id"
            loading={loading}
            pagination={{ showSizeChanger: true, showQuickJumper: true, pageSize: 10 }}
            dataSource={list}
            renderItem={(item) => (
              <List.Item
                actions={[
                  <Button
                    key="detail"
                    type="link"
                    onClick={() => getRecord({ data: { id: item.id, organizationId } })}
                  >
                    详情
                  </Button>,
                  <Dropdown
                    key="more"
                    menu={{
                      onClick: ({ key }) => handleMoreAction(key, item),
                      items: [
                        {
                          key: 'edit',
                          label: '编辑',
                        },
                        {
                          key: 'remove',
                          danger: true,
                          disabled: removing,
                          label: '移除',
                        },
                      ],
                    }}
                  >
                    <Button type="link">
                      更多 <DownOutlined />
                    </Button>
                  </Dropdown>,
                ]}
              >
                <List.Item.Meta
                  title={renderScriptTitle(item)}
                  description={extractSpeechScript(item.script) || '-'}
                />
                <ListContent data={item} />
              </List.Item>
            )}
          />
        </Card>
      </Flex>
      <ModalForm
        key={editRecord?.id || 'edit'}
        title={`编辑${resourceName}`}
        open={Boolean(editRecord)}
        width={560}
        initialValues={editRecord || {}}
        modalProps={{
          destroyOnHidden: true,
          okButtonProps: { loading: updating },
        }}
        onOpenChange={(open) => {
          if (!open) {
            setEditRecord(null);
          }
        }}
        onFinish={async (values) => {
          try {
            await updateRecord({ data: { id: editRecord.id, organizationId, ...values } });
            setEditRecord(null);
            return true;
          } catch {
            return false;
          }
        }}
      >
        {renderFormFields()}
      </ModalForm>
      <DrawerForm
        title={`${resourceName}详情`}
        open={Boolean(detailRecord)}
        onOpenChange={(open) => {
          if (!open) {
            setDetailRecord(null);
          }
        }}
        submitter={false}
        width={1024}
      >
        {detailRecord ? <ScriptArcPreview record={detailRecord} /> : null}
      </DrawerForm>
    </PageContainer>
  );
};

export default ScriptArcs;
